import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import moment from 'moment'
import {
  IBuffetGeneralReportDate,
  IBuffetGeneralReportResult,
} from 'core/api/dto/BuffetGeneralReportDto'
import { showStyleDimensionType } from '../../BuffetReportsVZLine'
import { useTranslation } from 'react-i18next'
import { IHeaderData } from 'components/AppTable/AppTableDndMobile'
import { formatNumber } from 'shared/lib/hooks/formatNumber'
import {
  ImagePreviewWrapper,
  ImagePreviewWrapperSizeEnum,
} from 'shared/ui/ImagePreviewWrapper/ImagePreviewWrapper'
import {
  mapComplianceToLabel,
  formatAiPredictions,
} from 'utils/complianceHelpers'

interface SubTable {
  item: IBuffetGeneralReportResult
  currentCol?: number
  itemIndex?: number
  onTouchStart?: ((e: React.TouchEvent) => void) | undefined
  onTouchMove?: ((e: React.TouchEvent) => void) | undefined
  width?: number
  toggleAllState?: number[]
  toggleAllStateFullLenght?: number
  headerData?: IHeaderData[]
}

type ToggleAllStateSub = {
  index: number
  active: boolean
}

const SubTableRepresentFullComponent = forwardRef<
  HTMLTableRowElement,
  SubTable
>(
  (
    {
      item,
      currentCol,
      itemIndex,
      onTouchStart,
      onTouchMove,
      width,
      toggleAllState,
      toggleAllStateFullLenght,
      headerData,
    }: SubTable,
    ref: React.Ref<HTMLTableRowElement>,
  ) => {
    const [toggleAllStateSub, setToggleAllStateSub] = useState<
      ToggleAllStateSub[]
    >([])
    const [, setSearchParams] = useSearchParams()
    const { t } = useTranslation()
    const toggleMap = useMemo(() => {
      const map = new Map<number, boolean>()
      toggleAllStateSub.forEach(({ index, active }) => map.set(index, active))
      return map
    }, [toggleAllStateSub])
    useEffect(() => {
      if (
        toggleAllState &&
        toggleAllState.find((el) => el === itemIndex) &&
        toggleAllStateFullLenght === toggleAllState.length
      ) {
        setToggleAllStateSub(
          item.dates.map((e, i) => ({ index: i, active: true })),
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleAllState])

    const onToggle = (index: number) => {
      setToggleAllStateSub((prev) => {
        const element = prev.find((el) => el.index === index)
        if (element) {
          return prev.map((el) =>
            el.index === index ? { ...el, active: !el.active } : el,
          )
        } else {
          return [...prev, { index: index, active: true }]
        }
      })
    }
    const getData = (data: (JSX.Element | null)[]) => {
      return data?.map((_, index) => {
        return data[`${(headerData && headerData[index].startPosition) || 0}`]
      })
    }
    const subRow = (
      itemSub: IBuffetGeneralReportDate,
      currentCol?: number,
      onTouchStart?: ((e: React.TouchEvent) => void) | undefined,
      onTouchMove?: ((e: React.TouchEvent) => void) | undefined,
      width?: number,
    ) => {
      return (
        <React.Fragment>
          {itemSub.dimensions.map((subItem, index) => {
            const moveFn = {
              onTouchStart: onTouchStart,
              onTouchMove: onTouchMove,
            }
            const { label: complianceLabel, className: complianceClass } =
              mapComplianceToLabel(subItem.complianceWithStandards)
            const data = [
              null,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.internalDishCategory}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  <span>{subItem.station}</span>
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div
                  className={`text-end no-wrap ${
                    subItem.isDishReplaced ? 'no-match' : ''
                  }`}
                >
                  <span>{subItem.isDishReplaced ? 'Заменa' : ''}</span>
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  <span>{moment.parseZone(subItem.createdDay).format('DD.MM.YYYY')}</span>
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {moment.parseZone(subItem.createdAt).format('HH:mm')}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.planMenuName}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.planMenuGuestType}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div
                  className={`text-end no-wrap ${
                    showStyleDimensionType[subItem.dimensionType]
                  }`}
                >
                  {t(subItem.dimensionType)}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.valueTypeDimension}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`} />
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.sequenceNumber}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {formatNumber(subItem.plannedWeight)}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.plannedPersonsAmount}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}></div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div
                  className={`text-end ${
                    subItem.milletIsDefault === true ? 'millet-is-default' : ''
                  }`}
                >
                  {subItem.millet}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {formatNumber(subItem.milletWeight)}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {formatNumber(subItem.dimensionWeight)}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {formatNumber(subItem.actualWeight)}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{subItem.actualPersonsAmount}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}></div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>{}</div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  {subItem.temperature
                    .toFixed(2)
                    .toString()
                    .replace(/[.]/g, ',')}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div
                  className={`text-end no-wrap ${
                    subItem.temperatureMatch ? 'match' : 'no-match'
                  }`}
                >
                  {subItem.temperatureMatch
                    ? `${t('Соответствует')}`
                    : `${t('Не соответствует')}`}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end`}>
                  <div className={`text-end`}>
                    <ImagePreviewWrapper
                      className={'report-image-preview'}
                      onClick={() => {
                        setSearchParams((sp) => {
                          const newSearchParams = new URLSearchParams(sp)
                          newSearchParams.set(
                            'report-image-id',
                            `${subItem.id}-dimension`,
                          )
                          return newSearchParams.toString()
                        })
                      }}
                      isReportPageItem={true}
                      image={subItem.imageOnS3 || subItem.image}
                      size={ImagePreviewWrapperSizeEnum.MEDIUM}
                    />
                  </div>
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-end no-wrap ${complianceClass}`}>
                  {complianceLabel}
                </div>
              </td>,
              <td className={'buffet-report-page-table-cell'} {...moveFn}>
                <div className={`text-start  ai-type`}>
                  {subItem.complianceWithStandards === 'Compliant'
                    ? ''
                    : formatAiPredictions(subItem)}
                </div>
              </td>,
            ]
            return (
              <tr
                ref={ref}
                key={`${index}-sub-${subItem.station}${subItem.id}`}
                style={{ top: `${56 + 40 + index * 40}px` }}
              >
                <td
                  className={`buffet-report-page-table-cell col-sticky-name-sub-time`}
                >
                  <div className="table-sub-data-ico-block">
                    <div className="sub-data">
                      <div style={{ width: 20 }}></div>
                      <i className="an-ico an-ico-corner-down-right" />
                      <span>{subItem.name}</span>
                    </div>
                  </div>
                </td>
                {currentCol
                  ? width && width < 600
                    ? data[currentCol]
                    : data
                        .slice(currentCol, currentCol + 3)
                        .map((item, index) => (
                          <React.Fragment key={`data-render-${index}`}>
                            {item}
                          </React.Fragment>
                        ))
                  : getData(data)?.map((item, index) => (
                      <React.Fragment key={`data-render-${index}`}>
                        {item}
                      </React.Fragment>
                    ))}
              </tr>
            )
          })}
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        {item.dates.map((subItem, index) => {
          const moveFn = {
            onTouchStart: onTouchStart,
            onTouchMove: onTouchMove,
          }
          const data = [
            null,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              {/* <div className={`text-end`}>{subItem.internalDishCategory}</div> */}
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {moment.parseZone(subItem.createdDay).format('DD.MM.YYYY')}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{subItem.dimensionsCount}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {subItem.plannedWeight
                  .toFixed(3)
                  .toString()
                  .replace(/[.]/g, ',')}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{subItem.plannedPersonsAmount}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {formatNumber(subItem.plannedWeightPerPerson)}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {formatNumber(subItem.dimensionWeight)}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {formatNumber(subItem.actualWeight)}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{subItem.actualPersonsAmount}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {formatNumber(subItem.actualWeightPerPerson)}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{subItem.personsAmountDiff}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {subItem.weightDiff?.toFixed(3).toString().replace(/[.]/g, ',')}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>
                {`${subItem.temperatureMin
                  .toFixed(2)
                  .toString()
                  .replace(/[.]/g, ',')}-${subItem.temperatureAvg
                  .toFixed(2)
                  .toString()
                  .replace(/[.]/g, ',')}-${subItem.temperatureMax
                  .toFixed(2)
                  .toString()
                  .replace(/[.]/g, ',')}`}
              </div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`} />
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{}</div>
            </td>,
            <td className={'buffet-report-page-table-cell'} {...moveFn}>
              <div className={`text-end`}>{}</div>
            </td>,
          ]
          return (
            <React.Fragment key={`${index}-sub-${item.name}`}>
              <tr
                key={`${index}-sub-${item.name}`}
                className={'buffet-report-page-table-category'}
              >
                <td
                  className={
                    'buffet-report-page-table-cell col-sticky-name-sub'
                  }
                >
                  <div className="table-sub-data-ico-block">
                    <div className="sub-data">
                      <i className="an-ico an-ico-corner-down-right" />
                      <div
                        className={`table-toggle-block${
                          !!toggleMap.get(index) ? ' active' : ''
                        }`}
                        onClick={() => onToggle(index)}
                      >
                        <i className="an-ico an-ico-arrow-down" />
                      </div>
                    </div>
                  </div>
                  <span>{item.name}</span>
                </td>
                {currentCol
                  ? width && width < 600
                    ? data[currentCol]
                    : data
                        .slice(currentCol, currentCol + 3)
                        .map((item, index) => (
                          <React.Fragment key={`data-render-${index}`}>
                            {item}
                          </React.Fragment>
                        ))
                  : getData(data).map((item, index) => (
                      <React.Fragment key={`data-render-${index}`}>
                        {item}
                      </React.Fragment>
                    ))}
              </tr>
              {!!toggleMap.get(index) &&
                subRow(subItem, currentCol, onTouchStart, onTouchMove, width)}
            </React.Fragment>
          )
        })}
      </React.Fragment>
    )
  },
)
function areEqual(prevProps: SubTable, nextProps: SubTable) {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.currentCol === nextProps.currentCol &&
    prevProps.itemIndex === nextProps.itemIndex &&
    prevProps.width === nextProps.width &&
    prevProps.toggleAllState === nextProps.toggleAllState &&
    prevProps.toggleAllStateFullLenght === nextProps.toggleAllStateFullLenght &&
    prevProps.headerData === nextProps.headerData
  )
}

//  Оборачиваем компонент
export const SubTableRepresentFull = React.memo(
  SubTableRepresentFullComponent,
  areEqual,
)
